import dataService from 'services/dataService';
import {
  apiDoctorPatientReadings,
  apiDoctorPatientReadingsCount,
  API_READINGS_ACTIONS,
  API_DOCTOR_INSURANCE_TYPES,
  ACTIVITY_CSV_REPORT,
  API_PATIENTS_READINGS_RESERVE,
} from 'services/dataService/resources';

const ITEMS_PER_PAGE = 100;
const defaultProfile = {
  name: '',
  title: '',
  mobileNumber: '',
  workNumber: '',
  npiNumber: '',
  professionType: '',
  primaryLanguage: '',
  secondaryLanguage: '',
  addressLine1: '',
  addressLine2: '',
  state: '',
  zip: '',
  alertEmail: false,
  alertSms: false,
  logoUrl: '',
  designation: '',
  authorGuid: '',
};
const INITIAL_STATE = {
  reviewed: false,
  onCallPatients: [],
  filters: {
    status: null,
    clinicState: null,
    readingType: null,
    patientGuid: null,
    clinicGuid: null,
    insuranceType: null,
    reverse: null,
    assignee: null,
  },
  unsavedComments: {},
  unsavedGeneralComment: {},
  sort: {
    time: false,
    name: null,
    readingType: null,
  },
  page: 1,
  itemsPerPage: ITEMS_PER_PAGE,
  data: [],
  totalPages: 0,
  totalCount: 0,
  dates: [],
  insuranceTypes: [],
  readingsCount: {
    normal: 0,
    risk: 0,
    critical: 0,
  },
  profile: defaultProfile,
};

const doctor = {
  state: INITIAL_STATE,
  reducers: {
    reset: (state) => ({
      ...INITIAL_STATE,
      dates: state.dates.length > 0 ? state.dates : [],
    }),
    setFilterDates: (state, payload) => ({
      ...state,
      dates: payload,
    }),
    setReviewed: (state, payload) => ({
      ...state,
      reviewed: payload,
    }),
    setPage: (state, payload) => ({
      ...state,
      page: payload < 1 ? 1 : payload,
    }),
    setTotalCountAndTotalPages: (state, payload) => ({
      ...state,
      totalCount: payload.totalCount,
      totalPages: payload.totalPages,
    }),
    setActivities: (state, payload) => ({
      ...state,
      data: payload,
    }),
    setFilters: (state, filters) => ({
      ...state,
      filters: { ...state.filters, ...filters },
    }),
    setProfile: (state, payload) => ({
      ...state,
      profile: {
        ...state.profile,
        ...payload,
      },
    }),
    setInsuranceTypes: (state, payload) => ({
      ...state,
      insuranceTypes: payload,
    }),
    setReadingsCount: (state, payload) => ({
      ...state,
      readingsCount: payload,
    }),
    setUnsavedComments: (state, payload) => ({
      ...state,
      unsavedComments: payload,
    }),
    setUnsavedGeneralComment: (state, payload) => ({
      ...state,
      unsavedGeneralComment: payload,
    }),
    toggleTimeSort: (state) => ({
      ...state,
      sort: {
        ...state.sort,
        time: !state.sort.time,
      },
    }),
    toggleNameSort: (state) => ({
      ...state,
      sort: {
        ...state.sort,
        name: !state.sort.name || state.sort.name === 'DESC' ? 'ASC' : 'DESC',
      },
    }),
    toggleReadingTypeSort: (state) => ({
      ...state,
      sort: {
        ...state.sort,
        readingType:
          !state.sort.readingType || state.sort.readingType === 'DESC'
            ? 'ASC'
            : 'DESC',
      },
    }),
    setOnCallPatients: (state, payload) => ({
      ...state,
      onCallPatients: payload,
    }),
  },
  effects: (dispatch) => ({
    async getActivities(
      { reviewed, status, items = ITEMS_PER_PAGE, page },
      state,
    ) {
      if (page) {
        dispatch.doctor.setPage(page);
      }
      if (reviewed) {
        dispatch.doctor.setReviewed(reviewed);
      }
      const {
        page: statePage,
        dates,
        filters,
        sort,
        reviewed: stateReviewed,
      } = state.doctor;
      const [startTime, endTime] = dates;
      if (status) {
        filters.status = status;
      }
      const { data, error } = await dataService.getList(
        apiDoctorPatientReadings(
          reviewed ?? stateReviewed,
          items,
          page ?? statePage,
          startTime,
          endTime,
          sort.time,
          sort.name,
          sort.readingType,
          filters,
        ),
      );
      if (error) {
        dispatch.doctor.setActivities([]);
      }
      if (data) {
        dispatch.doctor.setActivities(data?.data || []);
        const totalCount = data?.count || 0;
        const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
        dispatch.doctor.setTotalCountAndTotalPages({
          totalCount,
          totalPages,
        });
      } else {
        dispatch.doctor.setActivities([]);
        dispatch.doctor.setTotalCountAndTotalPages({
          totalCount: 0,
          totalPages: 0,
        });
      }
    },
    async getActivitiesCount(payload, state) {
      const { reviewed, dates, filters } = state.doctor;
      const [startTime, endTime] = dates;

      const { data } = await dataService.getList(
        apiDoctorPatientReadingsCount(reviewed, startTime, endTime, filters),
      );
      if (data) {
        dispatch.doctor.setReadingsCount(data);
      } else {
        dispatch.doctor.setReadingsCount({
          critical: 0,
          risk: 0,
          normal: 0,
        });
      }
    },
    async submitComment(postData, state) {
      const { error, data } = await dataService.createOne(
        API_READINGS_ACTIONS,
        postData,
      );
      if (!error) {
        const updatedData = state.doctor.data.map((item) =>
          item.id === data.readingId && item.deviceId === data.deviceId
            ? { ...item, comments: [data, ...item.comments] }
            : item,
        );
        dispatch.doctor.setActivities(updatedData);
        dispatch.doctor.setUnsavedComments({});
      }
      return { error, data };
    },
    async actionReview(postData, state) {
      const { error, data } = await dataService.createOne(
        API_READINGS_ACTIONS,
        postData,
      );
      if (!error) {
        const updatedData = state.doctor.data.map((item) => {
          if (item.id !== data.readingId || item.deviceId !== data.deviceId)
            return item;
          return {
            ...item,
            disabled: true,
          };
        });
        dispatch.doctor.setActivities(updatedData);
      }
      return { error, data };
    },
    async changeReviewed(payload, state) {
      const { reviewed } = state.doctor;
      dispatch.doctor.setPage(1);
      dispatch.doctor.setReviewed(!reviewed);
      dispatch.doctor.getActivities({});
      dispatch.doctor.getActivitiesCount();
    },
    async setActivitiesPage({ reviewed = false, pageOffset, goToPage }, state) {
      const { doctor: doctorState } = state;
      const { page, totalPages } = doctorState;
      const newPage = goToPage || page + pageOffset;

      dispatch.doctor.setPage(newPage <= totalPages ? newPage : totalPages);
      return dispatch.doctor.getActivities({
        page: newPage,
        reviewed: !reviewed ? doctorState.reviewed : reviewed,
      });
    },
    async toggleActivitiesTimeSort() {
      dispatch.doctor.toggleTimeSort();
      return dispatch.doctor.getActivities({});
    },
    async toggleActivitiesNameSort() {
      dispatch.doctor.toggleNameSort();
      return dispatch.doctor.getActivities({});
    },
    async toggleActivitiesReadingTypeSort() {
      dispatch.doctor.toggleReadingTypeSort();
      return dispatch.doctor.getActivities({});
    },
    async getInsuranceTypes() {
      const { data } = await dataService.getList(API_DOCTOR_INSURANCE_TYPES);
      if (data) {
        dispatch.doctor.setInsuranceTypes(data);
      }
    },
    applyFilters(filters) {
      Object.keys(filters).forEach((key) => {
        if (filters[key] === undefined) delete filters[key];
        else if (filters[key] === 'all') filters[key] = null;
      });
      dispatch.doctor.setFilters(filters);
      dispatch.doctor.setPage(Number(filters?.page) || 1);
    },
    applyTimeFilter(dates) {
      if (dates.length === 0) {
        dispatch.doctor.setFilterDates([]);
      } else {
        const [startDate, endDate] = dates;
        const startTime = startDate.getTime();
        const endTime = endDate.getTime();
        dispatch.doctor.setFilterDates([startTime, endTime]);
      }
      dispatch.doctor.setPage(1);
    },
    async getActivityCsvReport(payload, state) {
      const { dates, filters } = state.doctor;
      const [startTime, endTime] = dates;

      const reportFilters = {};
      Object.keys(filters).forEach((key) => {
        const filter = filters[key];
        if (filter) {
          reportFilters[key] = filter;
        }
      });
      const { error, data } = await dataService.createOne(ACTIVITY_CSV_REPORT, {
        ...reportFilters,
        startTime,
        endTime,
      });
      return { error, data };
    },
    addComment(key, state) {
      let comments;
      const { unsavedComments } = state.doctor;
      // eslint-disable-next-line prefer-const
      comments = { ...unsavedComments };
      if (!comments[key]) {
        comments[key] = true;
        dispatch.doctor.setUnsavedComments(comments);
      }
    },
    removeComment(key, state) {
      let comments;
      const { unsavedComments } = state.doctor;
      // eslint-disable-next-line prefer-const
      comments = { ...unsavedComments };
      if (comments[key]) {
        delete comments[key];
        dispatch.doctor.setUnsavedComments(comments);
      }
    },
    clearComments() {
      dispatch.doctor.setUnsavedComments({});
    },
    addGeneralComment(key, state) {
      let comments;
      const { unsavedGeneralComment } = state.doctor;
      // eslint-disable-next-line prefer-const
      comments = { ...unsavedGeneralComment };
      if (!comments[key]) {
        comments[key] = true;
        dispatch.doctor.setUnsavedGeneralComment(comments);
      }
    },
    removeGeneralComment(key, state) {
      let comments;
      const { unsavedGeneralComment } = state.doctor;
      // eslint-disable-next-line prefer-const
      comments = { ...unsavedGeneralComment };
      if (comments[key]) {
        delete comments[key];
        dispatch.doctor.setUnsavedGeneralComment(comments);
      }
    },
    clearGeneralComment() {
      dispatch.doctor.setUnsavedGeneralComment({});
    },
    addOnCallPatient(patientGuid, state) {
      const { onCallPatients } = state.doctor;
      if (!onCallPatients.includes(patientGuid)) {
        dispatch.doctor.setOnCallPatients([...onCallPatients, patientGuid]);
      }
    },
    removeOnCallPatient(patientGuid, state) {
      const { onCallPatients } = state.doctor;
      const index = onCallPatients.indexOf(patientGuid);
      if (index > -1) {
        onCallPatients.splice(index, 1);
      }
    },
    async togglePatientReserved(patientGuid) {
      const { data: response } = await dataService.createOne(
        API_PATIENTS_READINGS_RESERVE,
        {
          patientGuid,
        },
      );
      return dispatch.doctor.updateActivities(response.data);
    },
    updateActivities({ action, patientGuid, body }, state) {
      const { data } = state.doctor;
      const newData = data.map((item) => {
        if (item.patient.guid === patientGuid) {
          if (action === 'unassign') return { ...item, reserved: null };
          return { ...item, reserved: body };
        }
        return item;
      });
      dispatch.doctor.setActivities(newData);
    },
    clearActivities(readings, state) {
      const { data } = state.doctor;
      const newData = data.map((item) => {
        if (
          readings.some((reading) => reading.patientGuid === item.patient.guid)
        ) {
          return { ...item, reserved: null };
        }
        return item;
      });
      dispatch.doctor.setActivities(newData);
    },
  }),
};

export default doctor;
